import React, { useId, useState } from "react";
import { Button } from "@mui/material";
import { useSelector } from "react-redux";
import OutlinedTextInput from "../../../../components/custom-input/outlined-text-input";
import SecondarySelectLOLP from "../../../../components/custom-select/secondary-select-lo-lp";
import SecondaryButton from "../../../../components/buttons/secondary-button";
import CustomAlert from "../../../../components/custom-alert";
import LoaderModal from "../../../../components/modal/loader-modal";
import {
	useCreditVendorPublicKeyMutation,
	useGetCreditVendorDetailsForDuplicateCheckMutation,
	useSaveCreditVendorDetailsMutation,
	useUpdateCreditVendorDetailsMutation,
} from "../../../../features/credit-reissue/creditVendorsApiSlice";
import { selectUserDetails } from "../../../../features/auth/authSlice";
import useErrorMessages from "../../../../hooks/useErrorMessages";
import { encryptCreditVendorPassword } from "../../../../utils/encrypt-credit-vendor-password";
import { logErrorToCloudWatch } from "../../../../utils/rum";
import { COMPANY_ADMIN } from "../../../../utils/common-constants";
import styles from "./AddProviderForm.module.scss";

const PASSWORD_MASK = "***";
const SUBSCRIBER_ID_MASK = "******";

const AddProviderForm = ({
	providerOptions,
	adminConfiguredProviders = [],
	provider,
	organizationId,
	onSave,
	onCancel,
	isCompanyAdmin = false,
	isSubscriberIdReadOnly = false,
}) => {
	const formId = useId();
	const userDetails = useSelector(selectUserDetails);
	const isEditing = Boolean(provider);
	const [getCreditVendorPublicKey] = useCreditVendorPublicKeyMutation();
	const [getCreditVendorDetailsForDuplicateCheck] =
		useGetCreditVendorDetailsForDuplicateCheckMutation();
	const [saveCreditVendorDetails] = useSaveCreditVendorDetailsMutation();
	const [updateCreditVendorDetails] = useUpdateCreditVendorDetailsMutation();
	// Cover the complete public-key, encryption, and save sequence.
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const { getErrorMessage } = useErrorMessages();
	const existingEncryptedPassword = provider?.encryptedPassword ?? "";
	const existingSubscriberId = provider?.subscriberId ?? "";
	const [values, setValues] = useState({
		// New providers must explicitly choose a vendor. Selecting the first option
		// here bypasses the change handler that prepopulates the subscriber ID.
		creditVendor: provider?.name ?? "",
		userName: provider?.userName ?? "",
		password: existingEncryptedPassword ? PASSWORD_MASK : "",
		subscriberId: existingSubscriberId ? SUBSCRIBER_ID_MASK : "",
	});
	const [prepopulatedSubscriberId, setPrepopulatedSubscriberId] = useState("");
	const selectedProvider = providerOptions.find(
		(provider) => provider.value === values.creditVendor
	);
	const isGroupOneVendor = selectedProvider?.group === "Group 1";
	const passwordIsUnchanged =
		isEditing && existingEncryptedPassword && values.password === PASSWORD_MASK;
	const subscriberIdIsUnchanged =
		isEditing &&
		existingSubscriberId &&
		values.subscriberId === SUBSCRIBER_ID_MASK;
	const isValid = Boolean(
		selectedProvider &&
			values.userName.trim() &&
			(passwordIsUnchanged || values.password.trim())
	);
	const isSubscriberIdLocked =
		isEditing &&
		isSubscriberIdReadOnly &&
		provider?.setByAdmin &&
		Boolean(provider?.subscriberId);

	const handleChange = (field, value) => {
		if (field === "subscriberId") setPrepopulatedSubscriberId("");
		setValues((current) => ({ ...current, [field]: value }));
	};

	const handleCreditVendorChange = (event) => {
		const creditVendor = event.target.value;
		const adminConfiguredProvider =
			userDetails?.access_level !== COMPANY_ADMIN &&
			adminConfiguredProviders.find(
				(provider) =>
					(provider.credit_vendor ?? provider.creditVendor) === creditVendor &&
					provider.added_by_admin === true
			);
		const adminSubscriberId =
			adminConfiguredProvider?.subscriberId ??
			adminConfiguredProvider?.subscriber_id ??
			"";

		setPrepopulatedSubscriberId(adminSubscriberId);
		setValues((current) => ({
			...current,
			creditVendor,
			subscriberId: adminSubscriberId
				? SUBSCRIBER_ID_MASK
				: current.subscriberId === SUBSCRIBER_ID_MASK
					? ""
					: current.subscriberId,
		}));
	};

	const subscriberIdForSave = subscriberIdIsUnchanged
		? existingSubscriberId
		: prepopulatedSubscriberId && values.subscriberId === SUBSCRIBER_ID_MASK
			? prepopulatedSubscriberId
			: values.subscriberId;

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!isValid || isSaving) return;
		setIsSaving(true);
		setSaveError("");
		let savedProvider;

		try {
			if (!isEditing) {
				const existingVendors =
					await getCreditVendorDetailsForDuplicateCheck(
						organizationId
					).unwrap();
				const vendorName = values.creditVendor.trim().toLowerCase();
				const currentUserEmail = String(userDetails?.email ?? "")
					.trim()
					.toLowerCase();
				const isDuplicate =
					Boolean(currentUserEmail) &&
					existingVendors.some(
						(vendor) => {
							const isSameVendor =
								(vendor.credit_vendor ?? vendor.creditVendor)
									?.trim()
									.toLowerCase() === vendorName;
							const isSameUser =
								String(vendor.user_email ?? "").trim().toLowerCase() ===
								currentUserEmail;

							if (!isSameVendor) return false;

							return userDetails?.access_level === COMPANY_ADMIN
								? isSameUser || vendor.added_by_admin === true
								: isSameUser;
						}
					);
				if (isDuplicate) {
					setSaveError(
						"This credit vendor is already configured. Please edit the existing entry instead of adding a duplicate; contact TPO Portal Support if the issue continues."
					);
					return;
				}
			}

			let encryptedPassword = existingEncryptedPassword;
			if (!passwordIsUnchanged) {
				const { public_key } = await getCreditVendorPublicKey().unwrap();
				encryptedPassword = await encryptCreditVendorPassword(
					values.password,
					public_key
				);
			}
			const details = {
				creditVendor: values.creditVendor,
				userName: values.userName,
				subscriberId: subscriberIdForSave,
				encrypted_password: encryptedPassword,
				organization_id: organizationId,
				set_by_admin: isCompanyAdmin,
			};
			if (isEditing) {
				savedProvider = await updateCreditVendorDetails({
					id: provider.id,
					...details,
				}).unwrap();
			} else {
				savedProvider = await saveCreditVendorDetails({
					...details,
					user_email: userDetails?.email,
					added_by_admin: userDetails?.access_level === COMPANY_ADMIN,
				}).unwrap();
			}
		} catch (err) {
			const creditVendorDetailsError = getErrorMessage(
				"CREDIT_VENDOR_DETAILS_NOT_FOUND",
				err
			);
			console.log("Credit vendor details save error:", err);
			logErrorToCloudWatch({
				error_code: creditVendorDetailsError.code,
				title: creditVendorDetailsError.title,
				message: creditVendorDetailsError.message,
				error_response: err,
			});
			setSaveError(creditVendorDetailsError.message);
			return;
		} finally {
			setIsSaving(false);
		}

		onSave(values.creditVendor, {
			id: savedProvider?.id ?? savedProvider?.data?.id,
			userName: values.userName,
			user_email: userDetails?.email,
			subscriberId: subscriberIdForSave,
			setByAdmin: isCompanyAdmin,
		});
	};

	return (
		<form
			className={styles.form}
			aria-labelledby={`${formId}-title`}
			onSubmit={handleSubmit}
		>
			<h2 id={`${formId}-title`} className={styles.title}>
				{isEditing ? "Edit Provider" : "Add Provider"}
			</h2>
			<p className={styles.description}>
				{isEditing
					? "Update this provider's credentials. Enter a new password to replace the saved password."
					: "Select a credit provider and enter your credentials. Each provider is saved separately."}
			</p>
			<div className={styles.fields}>
				<div className={styles.field}>
					<label
						id={`simple-select-${formId}-provider-label`}
						htmlFor={`simple-select-${formId}-provider`}
						className={styles.selectField}
					>
						Credit Provider<span className={styles.required}>*</span>
					</label>
					<SecondarySelectLOLP
						id={`${formId}-provider`}
						value={values.creditVendor}
						handleChange={handleCreditVendorChange}
						options={providerOptions}
						placeholder="Select Credit Vendor"
						disabled={isSaving}
						extraClass={styles.input}
						listboxClassName={styles.dropdownOptions}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor={`${formId}-userName`}>
						<span>
							Username <span className={styles.required}>*</span>
						</span>
						<span className={styles.requiredBadge} aria-hidden="true">
							Required
						</span>
					</label>
					<OutlinedTextInput
						id={`${formId}-userName`}
						name="userName"
						disabled={isSaving}
						type="text"
						placeholder="Enter username"
						value={values.userName}
						handleChange={(event) =>
							handleChange("userName", event.target.value)
						}
						autoComplete="off"
						inputProps={{ required: true }}
						extraClass={styles.input}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor={`${formId}-password`}>
						<span>
							Password <span className={styles.required}>*</span>
						</span>
						<span className={styles.requiredBadge} aria-hidden="true">
							Required
						</span>
					</label>
					<OutlinedTextInput
						id={`${formId}-password`}
						name="password"
						disabled={isSaving}
						type="password"
						placeholder="••••••••••••"
						value={values.password}
						handleChange={(event) =>
							handleChange("password", event.target.value)
						}
						autoComplete="new-password"
						inputProps={{ required: true }}
						extraClass={styles.input}
					/>
				</div>
				{!isGroupOneVendor && (
					<div className={styles.field}>
					<label htmlFor={`${formId}-subscriberId`}>
						<span>Subscriber ID</span>
					</label>
					<OutlinedTextInput
						id={`${formId}-subscriberId`}
						name="subscriberId"
						disabled={isSaving || isSubscriberIdLocked}
						type="text"
						placeholder="Enter Subscriber ID"
						value={values.subscriberId}
						handleChange={(event) =>
							handleChange("subscriberId", event.target.value)
						}
						autoComplete="off"
						inputProps={{
							"aria-describedby": `${formId}-subscriber-help`,
						}}
						extraClass={styles.input}
					/>
					<div
						id={`${formId}-subscriber-help`}
						className={styles.subscriberHelp}
					>
						<p>Setting this applies to all users at your company.</p>
						<p>
							<span aria-hidden="true">🔒 </span>Saving this locks it for all
							users at your company.
						</p>
					</div>
					</div>
				)}
			</div>
			<p className={styles.information}>
				<span aria-hidden="true">ℹ </span>
				The credit report identifier is always entered at the loan level and is
				not stored here.
			</p>
			{saveError && (
				<CustomAlert
					extraClass={styles.customError}
					severity="error"
					variant="outlined"
					details={saveError}
				/>
			)}
			<div className={styles.actions}>
				<SecondaryButton
					text="Cancel"
					fontType="regular"
					onClick={onCancel}
					disabled={isSaving}
					extraClass={styles.cancelButton}
					extraTextClass={styles.buttonText}
				/>
				<Button
					type="submit"
					variant="contained"
					disabled={!isValid || isSaving}
					className={styles.saveButton}
				>
					{isEditing ? "Save Changes" : "Add Provider"}
				</Button>
			</div>
			<LoaderModal open={isSaving} text="Saving provider..." />
		</form>
	);
};

export default AddProviderForm;
